import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import Breadcrumb from "@/components/layout/Breadcrumb";
import { pandianList, type PandianItem } from "@/mocks/pandian";
import { useSeo } from "@/hooks/useSeo";

function PandianCard({ item }: { item: PandianItem }) {
  return (
    <article className="flex flex-col rounded-2xl border border-background-200 bg-background-50 p-5">
      <div className="flex items-center justify-between gap-3">
        <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-xs ${item.tone}`}>
          <i className={item.icon}></i>
          {item.tag}
        </span>
        <span className="whitespace-nowrap text-xs text-foreground-400">{item.span}</span>
      </div>
      <h3 className="mt-4 text-lg font-semibold leading-snug text-foreground-950">{item.title}</h3>
      <p className="mt-2 text-sm leading-6 text-foreground-600">{item.lead}</p>

      <ul className="mt-4 flex-1 space-y-2.5">
        {item.points.map((p) => (
          <li key={p} className="flex items-start gap-2 text-sm leading-6 text-foreground-700">
            <i className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${item.tone.split(" ")[0]}`}></i>
            <span>{p}</span>
          </li>
        ))}
      </ul>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-4">
        <span className="flex items-center gap-1 whitespace-nowrap text-xs text-foreground-500">
          <i className="ri-database-2-line text-primary-500"></i>
          {item.count}
        </span>
        {item.related.length > 0 && (
          <Link
            to={`/shijian/${item.related[0].slug}`}
            className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition hover:text-primary-700"
          >
            看配套系列时间线 <i className="ri-arrow-right-line"></i>
          </Link>
        )}
      </div>
    </article>
  );
}

export default function Pandian() {
  const [activeTag, setActiveTag] = useState("全部");
  const [kw, setKw] = useState("");

  const tags = useMemo(() => ["全部", ...Array.from(new Set(pandianList.map((p) => p.tag)))], []);
  const filtered = useMemo(() => {
    return pandianList.filter((p) => {
      const okTag = activeTag === "全部" || p.tag === activeTag;
      const okKw = !kw.trim() || p.title.includes(kw.trim()) || p.lead.includes(kw.trim()) || p.tag.includes(kw.trim());
      return okTag && okKw;
    });
  }, [activeTag, kw]);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "全网热瓜同类盘点库",
      description: "把塌房、声明、假唱、综艺剪辑等同类热瓜归拢成图谱的盘点库。",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://w01xfj.cn/" },
        { "@type": "ListItem", position: 2, name: "同类盘点", item: "https://w01xfj.cn/pandian" },
      ],
    },
  ];

  useSeo({
    title: "全网热瓜同类盘点库｜塌房·声明·假唱·综艺剪辑图谱",
    description:
      "全网热瓜同类盘点一锅端：塌房类型图谱、道歉声明话术库、假唱红黑榜、综艺恶意剪辑争议盘点与经纪公司回应话术对照，把多年相似剧情归拢成一张图谱一次看清。",
    canonical: "https://w01xfj.cn/pandian",
    jsonLd,
  });

  return (
    <>
      <Navbar />
      <main>
        <div className="mx-auto max-w-container px-4 pt-10 md:px-6">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "同类盘点" }]} />
        </div>

        <section className="mx-auto max-w-container px-4 pt-6 md:px-6">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">同类盘点</span>
              <h1 className="mt-2 text-3xl text-foreground-950 md:text-4xl">同类热瓜，一锅端给你</h1>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                塌房姿势、道歉模板、假唱界定、剪辑套路、粉圈大战、公关话术……把散落多年的相似剧情归拢比对，
                相似剧情和后续走向一次摸清。目录持续更新，欢迎投稿补全。
              </p>
            </div>
            <div className="flex w-full items-center gap-2 rounded-full border border-background-200 bg-background-50 px-4 py-2 lg:w-80">
              <i className="ri-search-line text-foreground-400"></i>
              <input
                value={kw}
                onChange={(e) => setKw(e.target.value)}
                placeholder="搜索盘点关键词…"
                aria-label="搜索同类盘点"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
              {kw && (
                <button type="button" onClick={() => setKw("")} className="text-foreground-400 hover:text-foreground-700" aria-label="清空搜索">
                  <i className="ri-close-circle-fill"></i>
                </button>
              )}
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-2">
            {tags.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setActiveTag(t)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition ${
                  activeTag === t
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 text-foreground-700 ring-1 ring-background-200 hover:bg-primary-100"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-container px-4 py-8 md:px-6">
          <div className="mb-5 flex items-center justify-between text-sm text-foreground-500">
            <span>
              共 <span className="font-semibold text-foreground-900">{filtered.length}</span> 篇盘点
            </span>
            <span className="hidden items-center gap-1 sm:flex">
              <i className="ri-refresh-line text-primary-500"></i> 随热点回溯持续补全
            </span>
          </div>
          {filtered.length > 0 ? (
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
              {filtered.map((p) => (
                <PandianCard key={p.id} item={p} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center rounded-3xl border border-dashed border-background-300 bg-background-100/50 py-16 text-center">
              <i className="ri-inbox-line text-3xl text-foreground-400"></i>
              <p className="mt-3 text-sm text-foreground-600">没有找到匹配的盘点，换个关键词试试？</p>
              <button
                type="button"
                onClick={() => {
                  setKw("");
                  setActiveTag("全部");
                }}
                className="mt-4 rounded-full bg-primary-500 px-5 py-2 text-sm text-background-50 transition hover:bg-primary-400"
              >
                重置筛选
              </button>
            </div>
          )}
        </section>

        <section className="mx-auto max-w-container px-4 pb-4 md:px-6">
          <div className="flex flex-col gap-4 rounded-3xl bg-secondary-100/70 p-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground-950">想看对应事件的完整过程？</h2>
              <p className="mt-1 text-sm text-foreground-600">盘点给你“同类图谱”，系列事件时间线给你“完整过程”，配合服用效果更佳。</p>
            </div>
            <Link to="/#series" className="inline-flex h-11 shrink-0 items-center justify-center whitespace-nowrap rounded-full bg-primary-500 px-5 text-sm font-medium text-background-50 transition hover:bg-primary-400">
              去追系列事件 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}